import { type FlexibleUnion, type Schema, TicketureApiError } from '@tix/openapi-sdk'

type FlowActionType = Schema<'action_input'>['type']

/**
 * Parsed structure of a flow action API error, based on the code and description of the TicketureApiError.
 *
 *
 * The code uses a dot notation and can have one of the following structures:
 * - validation.user_inputs.<action>
 * - validation.previous_inputs.<action>
 * - invalid_request
 *
 * The description uses a colon notation and can have one of the following structures:
 * - input validation: user input error: invalid date
 * - user input error: invalid date
 * - action response type does not match action type (i.e. just a string message)
 *
 * Source: [tix-api/flows/actions/errors.go#L23](https://github.com/stqry/tix-api/blob/main/flows/actions/errors.go#L23)
 *
 * We will parse those to an object structure that we can work with.
 *
 * @example
 * API response:
 * ```js
 * {
 *   _code: 'validation.user_inputs.pick_date',
 *   _description: 'input validation: user input error: invalid date',
 * }
 * ```
 *
 * Parses to:
 * ```js
 * {
 *   code.parsed: {
 *     code: 'validation',
 *     type: 'user_inputs',
 *     action: 'pick_date'
 *   },
 *   description.parsed: {
 *     code: 'input validation',
 *     type: 'user input error',
 *     message: 'invalid date'
 *   }
 * }
 * ```
 */
export interface ParsedFlowActionError {
  code: {
    raw?: string
    parsed: {
      code?: FlexibleUnion<'validation' | 'invalid_request'>
      type?: FlexibleUnion<'user_inputs' | 'previous_inputs'>
      action?: FlexibleUnion<FlowActionType>
    }
  }
  description: {
    raw?: string
    parsed: {
      code?: FlexibleUnion<'input validation' | 'previous_input validation' | 'user input error'>
      type?: FlexibleUnion<'user input error'>
      message: string
    }
  }
  context?: string
}

export function getErrorForAction(
  error: unknown,
  actionType: FlowActionType,
):
  | { rawError: unknown; parsedError?: never; message?: never }
  | { rawError: TicketureApiError; parsedError: ParsedFlowActionError; message?: string } {
  if (!(error instanceof TicketureApiError)) return { rawError: error }

  const parsedCode = parseCode(error.code)
  const parsedDescription = parseDescription(error.description)

  // Match validation errors with explicit action type in dot notation (e.g., validation.user_inputs.pick_date)
  const isValidationError =
    parsedCode.code === 'validation' &&
    parsedCode.type === 'user_inputs' &&
    // #TODO dalmo - what to do with validation.previous_inputs errors? it doesn't make sense to show these to the user (i.e. in the current action). Potentially could be handled in the store and automatically send the user back to the invalid step?? TBC with api devs
    // || parsedCode.type === 'previous_inputs'
    parsedCode.action === actionType

  // Match invalid_request errors that indicate user input errors (these don't encode action type, so we trust the api that these are actually related to the current action
  const isInvalidRequestUserError =
    parsedCode.code === 'invalid_request' && parsedDescription.code === 'user input error'

  const isErrorForAction = isValidationError || isInvalidRequestUserError

  if (!isErrorForAction) return { rawError: error }

  // #TODO dalmo - for now we surface the error message from the api to the user. That's not ideal, and should be dealed with. https://ticketure-team.atlassian.net/browse/TIC-5452
  const message = parsedDescription.message || error.description || 'An error occurred'

  return {
    rawError: error,
    parsedError: {
      code: { raw: error.code, parsed: parsedCode },
      description: { raw: error.description, parsed: parsedDescription },
      context: error.context,
    },
    message,
  }
}

/**
 * Parses a dot-separated code string into structured fields.
 * "validation.user_inputs.pick_date" -> { code: 'validation', type: 'user_inputs', action: 'pick_date' }
 */
function parseCode(rawCode?: string): ParsedFlowActionError['code']['parsed'] {
  const [code, type, action] = rawCode?.split('.').map((p) => p.trim()) ?? []
  return { code, type, action }
}

/**
 * Parses a colon-separated description string into structured fields.
 * "input validation: user input error: invalid date" -> { code: 'input validation', type: 'user input error', message: 'invalid date' }
 */
function parseDescription(description?: string): ParsedFlowActionError['description']['parsed'] {
  if (!description) return { message: '' }

  const parts = description.split(': ').map((p) => p.trim())

  if (parts.length === 1) return { message: parts[0] }
  if (parts.length === 2) return { code: parts[0], message: parts[1] }

  return {
    code: parts[0],
    type: parts[1],
    message: parts.slice(2).join(': '),
  }
}
